import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaShareAlt, FaEllipsisV, FaShoppingCart, FaEdit, FaTrash, FaExternalLinkAlt, FaEye } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import { filterRecipes } from '../../utils/recipeDetection';
import { getRecipeImage, defaultImage } from '../../utils/recipeFallbackImages';
import RecipePanel from './RecipePanel';
import RecipeForm from './RecipeForm';
import ServingsDialog from './ServingsDialog';
import RecipeReadView from './RecipeReadView';
import StarRating from '../StarRating';
import '../../styles/recipes/RecipesList.css';

// Zentrale Menü-Definition - immer genau 5 Einträge in fester Reihenfolge
const MENU_ITEMS = [
  { id: 'view', icon: FaEye, labelKey: 'recipes.viewRecipe', fallback: 'Rezept ansehen' },
  { id: 'shopping', icon: FaShoppingCart, labelKey: 'recipes.addToShoppingList', fallback: 'Zum Einkaufszettel' },
  { id: 'share', icon: FaShareAlt, labelKey: 'share', fallback: 'Teilen' },
  { id: 'edit', icon: FaEdit, labelKey: 'edit', fallback: 'Bearbeiten', manualOnly: true },
  { id: 'delete', icon: FaTrash, labelKey: 'delete', fallback: 'Löschen', manualOnly: true, danger: true },
];

const RecipesList = ({ bookmarks = [], isOpen, onClose }) => {
  const { t } = useLanguage();
  const {
    recipes,
    recipesLoading,
    fetchRecipes,
    deleteRecipe,
    extractRecipeFromUrl,
    getRecipeByBookmarkId
  } = useRecipe();
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [extractingRecipeId, setExtractingRecipeId] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  const [instagramBookmark, setInstagramBookmark] = useState(null); // Für Instagram-Fallback
  const [openMenuId, setOpenMenuId] = useState(null); // Für Overflow-Menü
  const [servingsDialogRecipe, setServingsDialogRecipe] = useState(null); // Rezept, für das Portionen abgefragt werden
  const [readViewRecipe, setReadViewRecipe] = useState(null); // Rezept für die Papier-Leseansicht
  const [readViewServings, setReadViewServings] = useState(4);
  const [showNotOnlineHint, setShowNotOnlineHint] = useState(false); // Hinweis-Dialog bei Klick auf manuelles Rezept
  const menuRef = useRef(null);
  const listContainerRef = useRef(null);

  // Menü schließen
  const closeMenu = useCallback(() => {
    setOpenMenuId(null);
  }, []);

  // Toggle Menü - öffnet neues oder schließt aktuelles
  const toggleMenu = useCallback((recipeId, event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpenMenuId(prev => prev === recipeId ? null : recipeId);
  }, []);

  // Cursor beim Öffnen sofort ins Suchfeld setzen (v.a. für Smartphones: sofort
  // lostippen können, ohne erst das Feld antippen zu müssen). Kleiner Timeout,
  // damit der Fokus nicht durch die Öffnen-Animation/das Layout wieder verloren geht.
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Close menu: outside click, ESC, scroll
  useEffect(() => {
    if (!openMenuId) return;

    // Outside click mit pointerdown
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    // ESC-Taste
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    // Scroll im Container
    const handleScroll = () => {
      closeMenu();
    };

    // Event-Listener hinzufügen (mit kleinem Delay für pointerdown)
    const timeoutId = setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown);
    }, 10);
    document.addEventListener('keydown', handleKeyDown);

    const listContainer = listContainerRef.current;
    if (listContainer) {
      listContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      if (listContainer) {
        listContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [openMenuId, closeMenu]);

  // Note: Recipes are loaded once on app start (RecipeContext)
  // No need to refetch on every tab switch

  // Filter to get only recipe bookmarks
  const recipeBookmarks = filterRecipes(bookmarks);

  // Apply search filter
  const filteredRecipes = recipeBookmarks.filter(recipe =>
    recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (recipe.description && recipe.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Stellt sicher, dass für ein Rezept (soweit möglich) Zutaten vorliegen -
  // lädt bei Online-Rezepten (Bookmarks) ohne Zutaten diese per Extraktion nach.
  // Wird sowohl von "Zum Einkaufszettel" als auch von "Rezept ansehen" genutzt,
  // damit die Extraktionslogik nur an einer Stelle gepflegt werden muss.
  // Gibt bei Erfolg das (ggf. angereicherte) Rezept zurück, bei Fehlschlag null
  // - dabei wurden extractionError bzw. der Instagram-Fallback bereits gesetzt.
  const ensureRecipeWithIngredients = async (recipe) => {
    setExtractionError(null);

    // Bereits vorhandene Zutaten (manuelle Rezepte, bereits extrahierte Online-Rezepte)
    if (recipe.ingredients && recipe.ingredients.length > 0) {
      return recipe;
    }

    // Kein Bookmark-Rezept mit URL: nichts zu extrahieren
    if (!recipe.url) {
      return recipe;
    }

    // Bookmark-Rezept ohne Zutaten: Erst Extraktion, dann zurückgeben
    setExtractingRecipeId(recipe.id);
    try {
      // 1. Prüfen ob bereits im lokalen State gecacht
      let recipeWithIngredients = recipes.find(r =>
        r.bookmarkId === String(recipe.id) && r.ingredients && r.ingredients.length > 0
      );

      if (!recipeWithIngredients) {
        // 2. Prüfen ob im Backend vorhanden (API call)
        try {
          const cachedRecipe = await getRecipeByBookmarkId(recipe.id);
          if (cachedRecipe && cachedRecipe.ingredients && cachedRecipe.ingredients.length > 0) {
            recipeWithIngredients = cachedRecipe;
          }
        } catch (err) {
          // Kein Cache vorhanden, weiter zur Extraktion
        }
      }

      if (!recipeWithIngredients) {
        // 3. Extraktion von URL
        recipeWithIngredients = await extractRecipeFromUrl(recipe.url, recipe.id, recipe.title);
      }

      if (recipeWithIngredients && recipeWithIngredients.ingredients && recipeWithIngredients.ingredients.length > 0) {
        return recipeWithIngredients;
      }

      setExtractionError(t('recipes.extractionError') || 'Keine Zutaten gefunden.');
      return null;
    } catch (err) {
      console.error('Error extracting recipe:', err);
      // Prüfe auf Instagram-Fehler
      if (err.message?.includes('INSTAGRAM_NOT_SUPPORTED') || recipe.url?.includes('instagram.com')) {
        setInstagramBookmark(recipe);
      } else {
        setExtractionError(t('recipes.extractionError') || 'Fehler beim Laden der Zutaten.');
      }
      return null;
    } finally {
      setExtractingRecipeId(null);
    }
  };

  const handleCreateShoppingList = async (recipe) => {
    const recipeWithIngredients = await ensureRecipeWithIngredients(recipe);
    if (recipeWithIngredients) {
      setSelectedRecipe(recipeWithIngredients);
      setIsPanelOpen(true);
    }
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    setSelectedRecipe(null);
  };

  // Portionenabfrage bestätigt -> Papier-Leseansicht öffnen
  const handleServingsConfirm = (servings) => {
    setReadViewRecipe(servingsDialogRecipe);
    setReadViewServings(servings);
    setServingsDialogRecipe(null);
  };

  const handleServingsCancel = () => {
    setServingsDialogRecipe(null);
  };

  const closeReadView = () => {
    setReadViewRecipe(null);
  };

  // Open form to create new recipe
  const handleCreateRecipe = () => {
    setEditingRecipe(null);
    setShowRecipeForm(true);
  };

  // Open form to edit existing recipe
  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setShowRecipeForm(true);
  };

  // Delete recipe with confirmation
  const handleDeleteRecipe = async (recipe) => {
    if (window.confirm(t('recipes.form.confirmDelete'))) {
      try {
        await deleteRecipe(recipe.id);
      } catch (error) {
        console.error('Error deleting recipe:', error);
      }
    }
  };

  // Close form and refresh
  const handleFormClose = () => {
    setShowRecipeForm(false);
    setEditingRecipe(null);
  };

  // Form submit success
  const handleFormSubmit = () => {
    setShowRecipeForm(false);
    setEditingRecipe(null);
    setInstagramBookmark(null);
    fetchRecipes();
  };

  // Instagram-Fallback: Manuelles Rezept erstellen
  const handleCreateManualFromInstagram = () => {
    if (instagramBookmark) {
      // Erstelle ein neues Rezept mit Instagram-Link als Referenz
      setEditingRecipe({
        title: instagramBookmark.title || 'Instagram Rezept',
        description: `Rezept von Instagram: ${instagramBookmark.url}`,
        sourceUrl: instagramBookmark.url,
        bookmarkId: instagramBookmark.id,
        baseServings: 2,
        ingredients: []
      });
      setShowRecipeForm(true);
      setInstagramBookmark(null);
    }
  };

  // Instagram-Fallback schließen
  const closeInstagramFallback = () => {
    setInstagramBookmark(null);
  };

  // Karten-Klick (nicht Hamburger-Menü): bei Online-Rezepten URL öffnen,
  // bei manuellen Rezepten Hinweis-Dialog zeigen (Ansehen läuft nur über das Menü)
  const handleCardClick = (recipe) => {
    if (recipe.isManual) {
      setShowNotOnlineHint(true);
      return;
    }
    const url = recipe.url || recipe.sourceUrl;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Menü-Aktionen - zentral definiert
  const handleMenuAction = async (actionId, recipe) => {
    closeMenu(); // Menü immer schließen bei Aktion

    switch (actionId) {
      case 'view': {
        // Immer Papier-Ansicht (ServingsDialog -> RecipeReadView), sowohl für
        // manuelle als auch für Online-Rezepte. Bei Online-Rezepten ohne
        // geladene Zutaten werden diese vorher (wie beim Einkaufszettel) extrahiert.
        const recipeWithIngredients = await ensureRecipeWithIngredients(recipe);
        if (recipeWithIngredients) {
          setServingsDialogRecipe(recipeWithIngredients);
        }
        break;
      }
      case 'shopping':
        handleCreateShoppingList(recipe);
        break;
      case 'share':
        if (navigator.share) {
          navigator.share({
            title: recipe.title,
            text: recipe.description || recipe.title,
            url: recipe.sourceUrl || recipe.url || window.location.href
          }).catch(() => {});
        }
        break;
      case 'edit':
        if (recipe.isManual) {
          handleEditRecipe(recipe);
        }
        break;
      case 'delete':
        if (recipe.isManual) {
          handleDeleteRecipe(recipe);
        }
        break;
      default:
        break;
    }
  };

  // Filter backend recipes by search term
  const filteredBackendRecipes = recipes.filter(recipe =>
    recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (recipe.description && recipe.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isOpen) return null;

  // Combine all recipes for display
  const allRecipes = [
    ...filteredBackendRecipes.map(r => ({ ...r, isManual: true })),
    ...filteredRecipes.map(r => ({ ...r, isManual: false }))
  ];

  return (
    <div className="dynamic-panel recipes-panel-modern">
      {/* Unified Search Section - Purple Bubbles style */}
      <div className="unified-search-section search-with-add">
        <input
          ref={searchInputRef}
          type="text"
          className="unified-search-input"
          placeholder={t('recipes.searchPlaceholder') || 'Rezepte suchen...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="clear-search"
            onClick={() => setSearchTerm('')}
            title={t('clearSearch') || 'Suche löschen'}
          >
            ×
          </button>
        )}
        <button
          className="search-add-btn"
          onClick={handleCreateRecipe}
          title={t('recipes.form.newRecipe') || 'Neues Rezept'}
          aria-label={t('recipes.form.newRecipe') || 'Neues Rezept'}
        >
          +
        </button>
      </div>

      {/* Header - App Bar Style */}
      <div className="quick-list-header">
        <div className="quick-list-header-left"></div>
        <div className="header-center"></div>
        <div className="quick-list-header-actions">
          <button
            className="quick-list-share-btn"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: t('recipes.title') || 'Rezepte',
                  url: window.location.href
                });
              }
            }}
            title={t('share.shareList') || 'Teilen'}
          >
            <FaShareAlt />
          </button>
        </div>
      </div>

      {/* Recipe Content */}
      <div className="recipes-content-modern" ref={listContainerRef}>
        {/* Loading State */}
        {recipesLoading && (
          <div className="recipes-loading-modern">
            <div className="loading-spinner"></div>
            <p>{t('loading') || 'Laden...'}</p>
          </div>
        )}

        {/* Recipe Cards List */}
        {!recipesLoading && allRecipes.length > 0 && (
          <div className="recipes-list-modern">
            {allRecipes.map(recipe => (
              <div
                key={`${recipe.isManual ? 'manual' : 'bookmark'}-${recipe.id}`}
                className={`recipe-card-modern ${openMenuId === recipe.id ? 'menu-open' : ''} ${!recipe.isManual ? 'recipe-card-clickable' : ''}`}
                onClick={() => handleCardClick(recipe)}
              >
                {/* Thumbnail - mit automatischem Fallback-Bild */}
                <div className="recipe-thumbnail">
                  <img
                    src={getRecipeImage(recipe)}
                    alt={recipe.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultImage;
                    }}
                  />
                </div>

                {/* Content */}
                <div className="recipe-card-content">
                  <h3 className="recipe-title-modern">{recipe.title}</h3>
                  <StarRating value={recipe.rating || 0} readOnly={true} maxStars={4} size="small" showEmpty={true} />
                  <div className="recipe-meta-modern">
                    {recipe.baseServings && (
                      <span className="meta-item">
                        <span className="meta-icon">👥</span>
                        {recipe.baseServings} {t('recipes.portions') || 'Portionen'}
                      </span>
                    )}
                    {recipe.prepTime && (
                      <span className="meta-item">
                        <span className="meta-icon">⏱️</span>
                        {recipe.prepTime} min
                      </span>
                    )}
                    {recipe.ingredients && recipe.ingredients.length > 0 && (
                      <span className="meta-item">
                        <span className="meta-icon">🥗</span>
                        {recipe.ingredients.length} {t('recipes.ingredients') || 'Zutaten'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Overflow Menu */}
                <div className="recipe-overflow-container" ref={openMenuId === recipe.id ? menuRef : null}>
                  <button
                    className="recipe-overflow-btn"
                    onClick={(e) => toggleMenu(recipe.id, e)}
                    aria-label="Menü"
                  >
                    <FaEllipsisV />
                  </button>

                  {openMenuId === recipe.id && (
                    <div
                      className="recipe-overflow-menu"
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      {MENU_ITEMS.map((item) => {
                        // Bearbeiten/Löschen nur für manuelle Rezepte anzeigen
                        if (item.manualOnly && !recipe.isManual) return null;

                        const IconComponent = item.icon;
                        return (
                          <button
                            key={item.id}
                            className={`overflow-menu-item ${item.danger ? 'overflow-menu-item-danger' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuAction(item.id, recipe);
                            }}
                          >
                            <IconComponent />
                            <span>{t(item.labelKey) || item.fallback}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Loading indicator for extraction */}
                {extractingRecipeId === recipe.id && (
                  <div className="recipe-extracting-overlay">
                    <div className="loading-spinner-small"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!recipesLoading && allRecipes.length === 0 && (
          <div className="recipes-empty-modern">
            <div className="empty-icon">🍳</div>
            <p className="empty-title">{t('recipes.noRecipes') || 'Keine Rezepte gefunden'}</p>
            <p className="empty-subtitle">{t('recipes.noRecipesHint') || 'Tippe auf + um ein neues Rezept zu erstellen'}</p>
          </div>
        )}
      </div>

      {/* Recipe Panel (for shopping list) */}
      {isPanelOpen && selectedRecipe && (
        <RecipePanel
          recipe={selectedRecipe}
          isOpen={isPanelOpen}
          onClose={closePanel}
        />
      )}

      {/* Hinweis-Dialog: Klick auf ein manuelles (nicht Online-) Rezept - muss aktiv bestätigt werden */}
      {showNotOnlineHint && (
        <div className="not-online-hint-overlay" onClick={() => setShowNotOnlineHint(false)}>
          <div className="not-online-hint-dialog" role="alertdialog" onClick={(e) => e.stopPropagation()}>
            <p className="not-online-hint-message">
              {t('recipes.notOnlineRecipeHint') || 'Dies ist kein Online-Rezept. Bitte den Menüpunkt „Rezept ansehen" verwenden.'}
            </p>
            <button className="btn btn-primary" onClick={() => setShowNotOnlineHint(false)}>
              {t('recipes.confirm') || 'Bestätigen'}
            </button>
          </div>
        </div>
      )}

      {/* Portionenabfrage vor der Papier-Leseansicht */}
      {servingsDialogRecipe && (
        <ServingsDialog
          recipe={servingsDialogRecipe}
          onConfirm={handleServingsConfirm}
          onCancel={handleServingsCancel}
        />
      )}

      {/* Papier-Leseansicht des Rezepts */}
      {readViewRecipe && (
        <RecipeReadView
          recipe={readViewRecipe}
          servings={readViewServings}
          onClose={closeReadView}
        />
      )}

      {/* Instagram Fallback Modal */}
      {instagramBookmark && (
        <div className="recipe-form-overlay">
          <div className="instagram-fallback-modal">
            <div className="instagram-fallback-header">
              <h3>📸 Instagram Rezept</h3>
              <button className="close-btn" onClick={closeInstagramFallback}>✕</button>
            </div>
            <div className="instagram-fallback-content">
              <div className="instagram-icon">📷</div>
              <p className="instagram-fallback-title">{instagramBookmark.title}</p>
              <p className="instagram-fallback-info">
                {t('recipes.instagramNotSupported') || 'Instagram unterstützt leider keine automatische Rezept-Extraktion. Du kannst das Rezept aber manuell anlegen.'}
              </p>
              <div className="instagram-fallback-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleCreateManualFromInstagram}
                >
                  ✏️ {t('recipes.createManually') || 'Rezept manuell anlegen'}
                </button>
                <a
                  href={instagramBookmark.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  🔗 {t('recipes.openInInstagram') || 'In Instagram öffnen'}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Form Modal */}
      {showRecipeForm && (
        <div className="recipe-form-overlay">
          <RecipeForm
            recipe={editingRecipe}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
            onDelete={handleFormSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default RecipesList;
